import {useEffect, useState} from "react";
import {useTranslation, Trans} from "react-i18next";
import {startVerification} from "../store/verification.slice";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "@nextui-org/react";
import Header from "./reusable/Header";
import {IdIcon} from "./icons/IdIcon";
import {CameraIcon3} from "./icons/CameraIcon3";
import PoweredBy from "./reusable/PoweredBy";
import {trackEvent} from "../utils/analytics";

const checklist = [
    {
        icon: <IdIcon height={20} width={20} stroke="var(--urtentic-sdk-primary, #255CFF)" />,
        key: "id",
    },
    {
        icon: <CameraIcon3 height={20} width={20} stroke="var(--urtentic-sdk-primary, #255CFF)" />,
        key: "camera",
    },
    {
        icon: (
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="var(--urtentic-sdk-primary, #255CFF)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="4"/>
                <line x1="12" y1="2" x2="12" y2="4"/>
                <line x1="12" y1="20" x2="12" y2="22"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="2" y1="12" x2="4" y2="12"/>
                <line x1="20" y1="12" x2="22" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
        ),
        key: "lighting",
    },
];

function Start({flowId, metadata, clientId, verificationId, redirectUrl, previewMode = false}) {
    const { credentials } = useSelector((store) => store.verification);
    const dispatch = useDispatch();
    const [previewNoticeShown, setPreviewNoticeShown] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        trackEvent("start_screen_viewed", { flowId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const start = () => {
        trackEvent("start_cta_clicked", { flowId });
        if (previewMode) {
            // No backend calls in preview mode — see Home.jsx's `preview` param.
            setPreviewNoticeShown(true);
            return;
        }
        dispatch(startVerification(flowId, metadata, clientId, verificationId, redirectUrl, credentials?.accessToken));
    };

    return (
        <div className="h-full flex flex-col">
            <Header/>
            <div className="flex flex-col p-5 h-full justify-between">

                {/* Hero */}
                <div className="flex flex-col items-center text-center gap-2">
                    <div className="heading text-2xl">{t("start.heading")}</div>
                    <div className="desc text-sm leading-relaxed" style={{color: "var(--urtentic-sdk-subtitle, #737373)"}}>
                        <Trans i18nKey="start.subheading" components={{ bold: <strong/> }} />
                    </div>
                </div>

                {/* Checklist */}
                <div className="flex flex-col gap-1">
                    <div
                        className="text-xs font-semibold uppercase tracking-widest mb-2 text-center"
                        style={{color: "var(--urtentic-sdk-body, #737373)"}}
                    >
                        {t("start.checklistTitle")}
                    </div>
                    {checklist.map((item) => (
                        <div
                            key={item.key}
                            className="flex flex-row items-start gap-3 p-4 rounded-lg background"
                        >
                            <div className="flex items-center justify-center rounded-full border icon-badge-bg h-9 w-9 shrink-0 mt-0.5">
                                {item.icon}
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <div
                                    className="font-semibold text-sm"
                                    style={{color: "var(--urtentic-sdk-body, #737373)"}}
                                >
                                    {t(`start.checklist.${item.key}.label`)}
                                </div>
                                <div className="desc text-xs leading-relaxed">{t(`start.checklist.${item.key}.hint`)}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA */}
                <div className="flex flex-col items-center gap-2">
                    <Button
                        fullWidth
                        radius="sm"
                        color="primary"
                        size="lg"
                        onClick={start}
                    >
                        {t("start.cta")}
                    </Button>
                    {previewMode && previewNoticeShown && (
                        <p className="desc text-center text-xs px-2">
                            {t("start.previewNotice")}
                        </p>
                    )}
                    <p className="desc text-center text-xs px-2">
                        <Trans
                            i18nKey="start.terms"
                            components={{
                                termsLink: (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text from the translation at runtime
                                    <a
                                        href="https://urtentic.com/privacy-policy?tab=terms"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-blue-500"
                                    />
                                ),
                                privacyLink: (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects the link text from the translation at runtime
                                    <a
                                        href="https://urtentic.com/privacy-policy?tab=privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-blue-500"
                                    />
                                ),
                            }}
                        />
                    </p>
                    <PoweredBy />
                </div>

            </div>
        </div>
    );
}

export default Start;
